import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, MATURITY, NATURE, BRANCH_COLORS, BRANCH_TITLES } from "@/lib/api";
import { PageHeader, Stat } from "@/components/Badges";
import { t } from "@/lib/i18n";

const Bar = ({ parts, total }) => (
  <div className="flex h-2.5 rounded-full overflow-hidden bg-secondary">{parts.map(([k, v, c]) => v > 0 && <span key={k} title={`${k}: ${v}`} style={{ width: `${(v / total) * 100}%`, background: c }} />)}</div>
);

export default function GapsPage() {
  const [g, setG] = useState(null);
  useEffect(() => { api.get("/analytics/gaps").then((r) => setG(r.data)); }, []);
  if (!g) return <div className="p-8 text-muted-foreground">{t("Analisi in corso…")}</div>;
  const ids = Object.keys(BRANCH_TITLES);
  const matrix = ids.map((from) => ids.map((to) => g.edges.filter((e) => e.from_branch === from && e.to_branch === to).length));

  return (
    <div data-testid="gaps-page">
      <PageHeader eyebrow={t("Meta-Layer · diagnostica dell'albero")} title={t("Analisi gap e sovrapposizioni")}
        subtitle={t("Dove il corpus è solido, dove è parziale, dove mancano file fonte e come i rami si parlano tra loro.")} />
      <div className="px-5 lg:px-8 py-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat testId="gaps-total-subs" label={t("Sotto-rami")} value={g.totale_sottorami} hint={`${g.maturity.consolidato} ${t("consolidati")}`} />
          <Stat testId="gaps-total-files" label={t("File nel corpus")} value={g.totale_file} hint={`${g.file_da_assimilare} ${t("da assimilare")}`} accent={g.file_da_assimilare ? "#F59E0B" : undefined} />
          <Stat testId="gaps-no-files" label={t("Sotto-rami senza fonte")} value={g.senza_file.length} hint={t("da collegare a nuovi file")} accent={g.senza_file.length ? "#EF4444" : "#10B981"} />
          <Stat testId="gaps-edges" label={t("Collegamenti trasversali")} value={g.edges.length} hint={t("relazioni tra sotto-rami")} />
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="surface p-5 space-y-3">
            <div className="eyebrow">{t("Distribuzione maturità")}</div>
            <Bar total={g.totale_sottorami} parts={Object.entries(g.maturity).map(([k, v]) => [MATURITY[k].label, v, MATURITY[k].text])} />
            <div className="flex gap-4 text-xs">{Object.entries(g.maturity).map(([k, v]) => <span key={k} className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: MATURITY[k].text }} />{MATURITY[k].label} <b className="num">{v}</b></span>)}</div>
          </div>
          <div className="surface p-5 space-y-3">
            <div className="eyebrow">{t("Distribuzione natura")}</div>
            <Bar total={g.totale_sottorami} parts={Object.entries(g.nature).map(([k, v]) => [NATURE[k].label, v, NATURE[k].text])} />
            <div className="flex gap-4 text-xs flex-wrap">{Object.entries(g.nature).map(([k, v]) => <span key={k} className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: NATURE[k].text }} />{NATURE[k].label} <b className="num">{v}</b></span>)}</div>
          </div>
        </div>

        <div className="surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground"><tr className="border-b border-border"><th className="text-left p-3">{t("Ramo")}</th><th className="text-right p-3">{t("Sotto-rami")}</th><th className="text-right p-3">{t("Consolidati")}</th><th className="text-right p-3">{t("File primari")}</th><th className="text-right p-3">{t("File trasversali")}</th><th className="text-right p-3">{t("Link")}</th><th className="text-left p-3">{t("Senza fonte")}</th></tr></thead>
            <tbody>{g.branches.map((b) => (
              <tr key={b.id} data-testid={`gaps-branch-${b.id}`} className="border-b border-border last:border-0">
                <td className="p-3"><Link to={`/?node=${b.id}`} className="flex items-center gap-2 font-medium hover:text-primary"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: b.color }} />{b.title}</Link></td>
                <td className="p-3 text-right num">{b.subs}</td><td className="p-3 text-right num">{b.consolidati}/{b.subs}</td>
                <td className="p-3 text-right num" style={{ color: b.files_primary === 0 ? "#EF4444" : undefined }}>{b.files_primary}</td>
                <td className="p-3 text-right num">{b.files_cross}</td><td className="p-3 text-right num">{b.cross_links}</td>
                <td className="p-3 text-xs text-muted-foreground max-w-xs">{b.senza_file.join(", ") || "—"}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>

        <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
          <div className="surface p-5">
            <div className="eyebrow mb-3">{t("Matrice dei collegamenti tra rami (da → a)")}</div>
            <div className="overflow-x-auto">
              <table className="text-[11px] num">
                <thead><tr><th></th>{ids.map((id) => <th key={id} className="p-1.5 font-normal" style={{ color: BRANCH_COLORS[id] }}>{BRANCH_TITLES[id].split(" ")[0]}</th>)}</tr></thead>
                <tbody>{ids.map((from, i) => (
                  <tr key={from}><td className="p-1.5 pr-3 text-left" style={{ color: BRANCH_COLORS[from] }}>{BRANCH_TITLES[from]}</td>
                    {matrix[i].map((v, j) => <td key={j} className="p-1.5 text-center"><span className="inline-flex h-7 w-7 items-center justify-center rounded" style={{ background: v ? `${BRANCH_COLORS[ids[j]]}${Math.min(30 + v * 25, 99).toString(16).padStart(2, "0")}` : "transparent", color: v ? "#fff" : "#443E38" }}>{v || "·"}</span></td>)}
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
          <div className="space-y-4">
            <div className="surface p-5">
              <div className="eyebrow mb-2">{t("Buchi: sotto-rami da approfondire")}</div>
              <ul className="space-y-1">{g.da_approfondire.map((s) => <li key={s.id}><Link data-testid={`gap-deepen-${s.id}`} to={`/?node=${s.id}`} className="text-sm flex items-center gap-2 hover:text-primary"><span className="h-2 w-2 rounded-full" style={{ background: BRANCH_COLORS[s.branch_id] }} />{s.title}</Link></li>)}</ul>
            </div>
            <div className="surface p-5">
              <div className="eyebrow mb-2">{t("Sovrapposizioni: file che servono più rami")}</div>
              <ul className="space-y-1.5">{g.file_trasversali.map((f) => <li key={f.id} className="text-sm"><div className="font-medium text-xs">{f.name}</div><div className="flex gap-1 mt-0.5">{f.branches.map((b) => <span key={b} className="text-[10px] px-1.5 rounded" style={{ background: `${BRANCH_COLORS[b]}22`, color: BRANCH_COLORS[b] }}>{BRANCH_TITLES[b]}</span>)}</div></li>)}</ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
